// components/portal/SourceDoctrineModal.tsx
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, Table2, X } from '@/components/ui/lucide-memo';
import type { ScoringCriterion } from '#types/types';
import { sourceDoctrineStructure } from '#lib/sourceDoctrineTemplate';

export type DoctrineScope = 'portfolio' | 'asset';

const scopeLabel = (scope: DoctrineScope, lang: string): string => {
  if (lang === 'fr') {
    return scope === 'asset' ? 'Actif' : 'Portfolio';
  }
  return scope === 'asset' ? 'Asset' : 'Portfolio';
};

/**
 * Default agent-instruction doctrine, seeded per scope. Starter content the user (or the agent,
 * from the chat) is meant to enrich with their own sourcing strategy: the point is to give the
 * sourcing agent maximum context. On an asset launch this seed is what gets written to the new
 * shortlist's own doctrine document, so it is a real starting text, not a placeholder.
 */
export const defaultDoctrineInstructions = (scope: DoctrineScope, lang: string): string => {
  const level = scope === 'asset' ? 'this asset' : 'this portfolio';
  return sourceDoctrineStructure(level, `Sourcing doctrine, ${scopeLabel(scope, lang)}`);
};

const inputBase =
  'w-full bg-transparent text-[13px] text-[#D4D4D4] placeholder:text-[#565656] focus:outline-none';

/** Hoisted: an inline literal would be a fresh plugin array on every render of the doc pane. */
const REMARK_PLUGINS = [remarkGfm];

/**
 * The scoring grid, read-only: one grid for every asset and every run (lib/leasing/briefCriteria.ts).
 * Only a run's weights move, through the agent (SET_RUN_WEIGHTS).
 */
const RubricTable = ({ value, lang }: { value: ScoringCriterion[]; lang: string }) => {
  const fr = lang === 'fr';
  const total = value.reduce((sum, c) => sum + (Number.isFinite(c.weight) ? c.weight : 0), 0);
  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <h2 className="font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>{fr ? 'Grille de scoring' : 'Scoring grid'}</h2>
        <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 13 }}>
          {fr
            ? 'La même grille pour chaque actif et chaque run. Demandez à l’agent de changer le poids d’un critère pour ce run.'
            : 'The same grid for every asset and every run. Ask the agent to change a criterion’s weight for this run.'}
        </p>
      </div>
      <div className="overflow-hidden rounded-[16px] border border-[#262626]">
        {value.map((criterion) => {
          const [name, ...rest] = criterion.label.split(':');
          return (
            <div key={criterion.id} className="grid grid-cols-[1fr_64px] items-start gap-3 border-b border-[#262626] px-4 py-3 last:border-b-0">
              <div className="min-w-0">
                <div className="font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>{rest.length ? name!.trim() : criterion.label}</div>
                {rest.length ? <div className="mt-0.5 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{rest.join(':').trim()}</div> : null}
              </div>
              <div className="text-right font-mono text-[#ECECEC]" style={{ fontSize: 13 }}>{criterion.weight}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-right text-[#8E8E8E]" style={{ fontSize: 12 }}>
        {fr ? 'Total des points' : 'Total points'}: <span className="font-mono text-[#ECECEC]">{total}</span>
      </div>
    </div>
  );
};

/** Notion-style markdown page for the free-form agent-instructions doctrine (".md"). */
export const markdownComponents = {
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="mb-3 mt-1 text-[24px] font-bold text-[#E8E8E8]" {...props} />
  ),
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="mb-2 mt-6 text-[17px] font-semibold text-[#E5E5E5]" {...props} />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="mb-1.5 mt-4 text-[14px] font-semibold text-[#D4D4D4]" {...props} />
  ),
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="mb-3 text-[14px] leading-relaxed text-[#B5B5B5]" {...props} />
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="mb-3 ml-5 list-disc space-y-1 text-[14px] leading-relaxed text-[#B5B5B5] marker:text-[#6E6E6E]" {...props} />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="mb-3 ml-5 list-decimal space-y-1 text-[14px] leading-relaxed text-[#B5B5B5] marker:text-[#6E6E6E]" {...props} />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => <li className="pl-1" {...props} />,
  strong: (props: React.HTMLAttributes<HTMLElement>) => <strong className="font-semibold text-[#E5E5E5]" {...props} />,
  em: (props: React.HTMLAttributes<HTMLElement>) => <em className="italic text-[#909090]" {...props} />,
  blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="my-3 border-l-2 border-[#3B82F6]/50 pl-3 text-[14px] italic text-[#909090]" {...props} />
  ),
  code: (props: React.HTMLAttributes<HTMLElement>) => (
    <code className="rounded bg-[#262626] px-1.5 py-0.5 font-mono text-[12px] text-[#D4D4D4]" {...props} />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => <span className="text-[#3B82F6]" {...props} />,
  hr: () => <hr className="my-5 border-[#2A2A2A]" />,
};

const MarkdownDoc = ({
  value,
  onChange,
  lang,
}: {
  value: string;
  onChange: (next: string) => void;
  lang: string;
}) => {
  const [editing, setEditing] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3">
        <h2 className="text-[15px] font-semibold text-[#E8E8E8]">
          {lang === 'fr' ? 'Instructions pour l’agent' : 'Agent instructions'}
        </h2>
        <p className="mt-1 text-[13px] text-[#909090]">
          {lang === 'fr'
            ? 'Contexte stratégique en texte libre, double-cliquez pour éditer.'
            : 'Free-form strategic context, double-click to edit.'}
        </p>
      </div>

      {editing ? (
        <textarea
          value={value}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          spellCheck={false}
          className="min-h-0 flex-1 resize-none rounded-[10px] border border-[#262626] bg-[#141414] p-4 font-mono text-[13px] leading-relaxed text-[#D4D4D4] placeholder:text-[#565656] focus:border-[#3B82F6]/40 focus:outline-none"
          placeholder={lang === 'fr' ? 'Rédigez la doctrine…' : 'Write the doctrine…'}
        />
      ) : (
        <div
          onDoubleClick={() => setEditing(true)}
          title={lang === 'fr' ? 'Double-cliquez pour éditer' : 'Double-click to edit'}
          className="min-h-0 flex-1 cursor-text overflow-y-auto rounded-[10px] border border-[#1F1F1F] bg-[#141414] px-6 py-5"
        >
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
            {value}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

type DoctrineFileId = 'rubric' | 'instructions';

export const SourceDoctrineModal = ({
  open,
  onClose,
  scope,
  lang,
  rubric,
  instructions,
  onInstructionsChange,
}: {
  open: boolean;
  onClose: () => void;
  scope: DoctrineScope;
  lang: string;
  rubric: ScoringCriterion[];
  instructions: string;
  onInstructionsChange: (next: string) => void;
}) => {
  const [activeFile, setActiveFile] = useState<DoctrineFileId>('rubric');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const files = useMemo(
    () => [
      {
        id: 'rubric' as const,
        name: 'scoring-rubric.csv',
        label: lang === 'fr' ? 'Grille de scoring' : 'Scoring grid',
        Icon: Table2,
      },
      {
        id: 'instructions' as const,
        name: 'agent-instructions.md',
        label: lang === 'fr' ? 'Instructions agent' : 'Agent instructions',
        Icon: BookOpen,
      },
    ],
    [lang]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[82vh] w-full max-w-[980px] overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left sidebar, doctrine file navigation */}
        <aside className="flex w-[240px] shrink-0 flex-col border-r border-[#262626] bg-[#161616] p-3">
          <div className="px-2 pb-3 pt-1">
            <div className="text-[13px] font-semibold text-[#E8E8E8]">
              {lang === 'fr' ? 'Doctrine de sourcing' : 'Sourcing doctrine'}
            </div>
            <div className="mt-0.5 text-[12px] text-[#6E6E6E]">{scopeLabel(scope, lang)}</div>
          </div>
          <nav className="flex flex-col gap-0.5">
            {files.map((file) => {
              const active = activeFile === file.id;
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => setActiveFile(file.id)}
                  className={
                    'flex items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-left transition-colors ' +
                    (active ? 'bg-[#242424]' : 'hover:bg-[#1F1F1F]')
                  }
                >
                  <file.Icon size={15} className={active ? 'text-[#D4D4D4]' : 'text-[#909090]'} />
                  <span className="flex min-w-0 flex-col">
                    <span className={'truncate text-[13px] ' + (active ? 'text-[#E8E8E8]' : 'text-[#B5B5B5]')}>
                      {file.label}
                    </span>
                    <span className="truncate font-mono text-[10px] text-[#565656]">{file.name}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex shrink-0 items-center justify-between border-b border-[#262626] px-5 py-3">
            <div className="flex items-center gap-2 font-mono text-[12px] text-[#909090]">
              {activeFile === 'rubric' ? <Table2 size={14} /> : <BookOpen size={14} />}
              {activeFile === 'rubric' ? 'scoring-rubric.csv' : 'agent-instructions.md'}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={lang === 'fr' ? 'Fermer' : 'Close'}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#909090] transition-colors hover:bg-[#2A2A2A] hover:text-[#E8E8E8]"
            >
              <X size={16} />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {activeFile === 'rubric' ? (
              <RubricTable value={rubric} lang={lang} />
            ) : (
              <MarkdownDoc value={instructions} onChange={onInstructionsChange} lang={lang} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact entry-point card that replaces the inline rubric editor in the setup
 * panel: a document glyph with orbiting dots. Clicking opens the doctrine modal.
 */
export const DoctrineEntryCard = ({
  onOpen,
  lang,
}: {
  onOpen: () => void;
  lang: string;
}) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={lang === 'fr' ? 'Ouvrir la doctrine' : 'Open doctrine'}
      className="group flex w-full items-center justify-center rounded-[12px] border border-dashed border-[#3A3A3A] bg-[#161616] py-6 transition-colors hover:border-[#5A5A5A] hover:bg-[#1B1B1B]"
    >
      {/* Doc icon with orbiting dots, centered */}
      <span className="relative flex h-11 w-11 items-center justify-center">
        <span className="pointer-events-none absolute inset-0">
          <span className="absolute left-1/2 top-0 h-1 w-1 -translate-x-1/2 rounded-full bg-[#4D4D4D] transition-colors group-hover:bg-[#6E6E6E]" />
          <span className="absolute right-0 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full bg-[#3A3A3A] transition-colors group-hover:bg-[#6E6E6E]" />
          <span className="absolute bottom-0 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#3A3A3A] transition-colors group-hover:bg-[#6E6E6E]" />
          <span className="absolute left-0 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full bg-[#4D4D4D] transition-colors group-hover:bg-[#6E6E6E]" />
          <span className="absolute right-1.5 top-1.5 h-[3px] w-[3px] rounded-full bg-[#333333] transition-colors group-hover:bg-[#565656]" />
          <span className="absolute bottom-1.5 left-1.5 h-[3px] w-[3px] rounded-full bg-[#333333] transition-colors group-hover:bg-[#565656]" />
        </span>
        <span className="flex h-7 w-7 items-center justify-center rounded-[7px] bg-[#232323] text-[#B5B5B5] transition-colors group-hover:text-[#E8E8E8]">
          <BookOpen size={15} />
        </span>
      </span>
    </button>
  );
};
